import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import DropdownAction from 'components/DropdownAction';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import Pill from 'components/Pill';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import Table from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import Truncator from 'components/Truncator';
import {H1} from 'components/Typography';
import useBreakpoint from 'hooks/useBreakpoint';
import {FC} from 'react';
import {useBackend} from 'state/backend';
import {twMerge} from 'tailwind-merge';
import {jt, t} from 'ttag';
import getPreferredLanguage from 'utils/locale';
import updateUrlParamsAndReload from 'utils/pagination';
import {setUrl} from 'utils/request';
import {onApiSuccess} from 'utils/request-axios/utils';

import {useDeleteCustomTemplate} from '../services';
import {CustomTemplatesViewBackendData} from '../types';
import Filter from './components/filter';

const LINKS = {
    seeDetailsCustomTemplates:
        'https://help.makeleaps.jp/ja/collections/3609246-ml-ml4sf-%E3%82%AB%E3%82%B9%E3%82%BF%E3%83%A0%E3%83%86%E3%83%B3%E3%83%97%E3%83%AC%E3%83%BC%E3%83%88?segment_aid=3724869c-d8bc-4b06-b665-5db70fc34a19',

    seeDetailsMlStockCustomTemplates:
        'https://help.makeleaps.jp/ja/articles/5569892-%E6%A8%99%E6%BA%96%E3%83%86%E3%83%B3%E3%83%97%E3%83%AC%E3%83%BC%E3%83%88%E3%82%92%E3%82%AB%E3%82%B9%E3%82%BF%E3%83%9E%E3%82%A4%E3%82%BA%E3%81%99%E3%82%8B-%E6%97%A9%E6%9C%9F%E3%82%A2%E3%82%AF%E3%82%BB%E3%82%B9?segment_aid=3724869c-d8bc-4b06-b665-5db70fc34a19',
};
const Page: FC = () => {
    const {
        data: {
            canCreate,
            customTemplates,
            documentTypes,
            mlStockCustomTemplates,
            paginationCustomTemplates,
            paginationMlStockCustomTemplates,
        },
        flags,
    } = useBackend<CustomTemplatesViewBackendData>();

    const isAboveBreakpoint = useBreakpoint('lgWide');

    const apiDelete = useDeleteCustomTemplate();

    const handleDelete = async (mid: string) => {
        await apiDelete.mutateAsync({mid}).then(
            onApiSuccess({
                onRedirect: (response) => {
                    if (response.name === 'customtemplate_list_view') {
                        return setUrl(window.Urls.customtemplate_list_view());
                    }

                    throw new Error('Unknown response');
                },
            }),
        );
    };

    const seeDetailsLinks = {
        customTemplates: (
            <Link
                href={LINKS.seeDetailsCustomTemplates}
                isOpenInNewTab={true}
                key="customTemplates"
            >{t`Help Center`}</Link>
        ),
        mlStockCustomTemplates: (
            <Link
                href={LINKS.seeDetailsMlStockCustomTemplates}
                isOpenInNewTab={true}
                key="mlStockCustomTemplates"
            >{t`Help Center`}</Link>
        ),
    };

    return (
        <>
            <DjangoMessages className="mb-4" />
            <NavigationTabs.DocumentSettings selection="customTemplates" />
            <H1>{t`Custom Templates`}</H1>
            <SectionHeader
                level={2}
                subtitle={jt`New templates which you control everything from layout to colors. See the ${seeDetailsLinks.customTemplates} (Japanese site).`}
                title={t`User-Defined Templates`}
            />
            <div className="mb-4 flex items-end justify-between">
                <Filter
                    filterFormValue="filter_file_based_template"
                    isAboveBreakpoint={isAboveBreakpoint}
                />
                <TooltipWrapper
                    message={!canCreate.allowed && canCreate.failureReason}
                >
                    <DropdownAction
                        disabled={!canCreate.allowed}
                        label={t`Create Template`}
                        layoutMethod="right-corner"
                        size="sm"
                    >
                        {documentTypes.map(
                            (documentType) =>
                                documentType.value && (
                                    <DropdownAction.Link
                                        href={window.Urls.file_based_customtemplate_create_view(
                                            documentType.value,
                                        )}
                                        key={documentType.value}
                                    >
                                        {documentType.label}
                                    </DropdownAction.Link>
                                ),
                        )}
                    </DropdownAction>
                </TooltipWrapper>
            </div>
            <Table classNameTable="table-fixed" hasStripes={true}>
                <Table.Header className="text-nowrap">
                    <Table.HeaderRow>
                        <Table.Column>{t`Display Name`}</Table.Column>
                        <Table.Column>{t`Document Type`}</Table.Column>
                        <Table.Column className="w-24">{t`Status`}</Table.Column>
                        <Table.Column
                            className={twMerge(
                                !isAboveBreakpoint &&
                                    getPreferredLanguage() === 'en' &&
                                    'w-60',
                            )}
                        />
                    </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                    {customTemplates.length === 0 ? (
                        <Table.Row>
                            <Table.Cell colSpan={4}>
                                <div className="px-5 pb-7 pt-10 text-center">
                                    {t`No User-Defined Templates found.`}
                                    <br />
                                    {t`Try changing your search terms or make a new Template.`}
                                    <TooltipWrapper
                                        message={
                                            !canCreate.allowed &&
                                            canCreate.failureReason
                                        }
                                    >
                                        <DropdownAction
                                            classNameAction="w-fit m-auto mt-4"
                                            disabled={!canCreate.allowed}
                                            label={t`Create Template`}
                                            layoutMethod="right-corner"
                                            size="sm"
                                        >
                                            {documentTypes.map(
                                                (documentType) =>
                                                    documentType.value && (
                                                        <DropdownAction.Link
                                                            href={window.Urls.file_based_customtemplate_create_view(
                                                                documentType.value,
                                                            )}
                                                            key={
                                                                documentType.value
                                                            }
                                                        >
                                                            {documentType.label}
                                                        </DropdownAction.Link>
                                                    ),
                                            )}
                                        </DropdownAction>
                                    </TooltipWrapper>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    ) : (
                        customTemplates.map((template) => (
                            <Table.Row key={template.mid}>
                                <Table.Cell>
                                    <Truncator
                                        href={template.urls.viewUrl}
                                        text={template.displayName}
                                        truncateAt="middle"
                                    />
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap">
                                    <Truncator
                                        text={template.documentType}
                                        truncateAt="middle"
                                    />
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap">
                                    {template.isArchived ? (
                                        <Pill
                                            className="inline-block"
                                            color={'grey_dark'}
                                            label={t`Archived`}
                                        />
                                    ) : template.isDraft ? (
                                        <Pill
                                            className="inline-block"
                                            color="grey"
                                            label={t`Draft`}
                                        ></Pill>
                                    ) : (
                                        <Pill
                                            className="inline-block"
                                            color="green"
                                            label={t`Published`}
                                        ></Pill>
                                    )}
                                </Table.Cell>
                                <Table.Cell className="flex items-center justify-end gap-2">
                                    {template.isDraft && (
                                        <TooltipWrapper
                                            message={
                                                !template.rights.canPublish
                                                    .allowed &&
                                                template.rights.canPublish
                                                    .failureReason
                                            }
                                        >
                                            <LinkButton
                                                disabled={
                                                    !template.rights.canPublish
                                                        .allowed
                                                }
                                                href={template.urls.publishUrl}
                                                kind="secondary"
                                                size="xs"
                                            >{t`Publish`}</LinkButton>
                                        </TooltipWrapper>
                                    )}
                                    <TooltipWrapper
                                        message={
                                            !template.isDraft
                                                ? t`The Custom Template has been published`
                                                : !template.rights.canEdit
                                                        .allowed
                                                  ? template.rights.canEdit
                                                        .failureReason
                                                  : ''
                                        }
                                    >
                                        <LinkButton
                                            disabled={
                                                !template.rights.canEdit.allowed
                                            }
                                            href={template.urls.editUrl}
                                            kind="secondary"
                                            size="xs"
                                        >{t`Edit`}</LinkButton>
                                    </TooltipWrapper>
                                    <TooltipWrapper
                                        message={
                                            !canCreate.allowed &&
                                            canCreate.failureReason
                                        }
                                    >
                                        <LinkButton
                                            disabled={!canCreate.allowed}
                                            href={template.urls.duplicateUrl}
                                            kind="secondary"
                                            size="xs"
                                        >{t`Duplicate`}</LinkButton>
                                    </TooltipWrapper>
                                    <TooltipWrapper
                                        message={
                                            !template.rights.canDelete
                                                .allowed &&
                                            template.rights.canDelete
                                                .failureReason
                                        }
                                    >
                                        <Button
                                            className="px-1.5"
                                            disabled={
                                                !template.rights.canDelete
                                                    .allowed
                                            }
                                            icon="trash"
                                            kind="secondary"
                                            onClick={() =>
                                                handleDelete(template.mid)
                                            }
                                            size="xs"
                                        />
                                    </TooltipWrapper>
                                </Table.Cell>
                            </Table.Row>
                        ))
                    )}
                </Table.Body>
            </Table>
            <TablePagination
                className="mb-5 mt-2"
                hideForSinglePage={true}
                isAmbigousStop={true}
                onChange={(page) => updateUrlParamsAndReload('page', page)}
                onFirst={() => updateUrlParamsAndReload('page', 1)}
                onLast={() => updateUrlParamsAndReload('page', 'last')}
                pageCurrent={paginationCustomTemplates.currentPage}
                pageStop={paginationCustomTemplates.numPages}
                paginationPadding={paginationCustomTemplates.windowPages}
            />

            {flags.customtemplates && (
                <>
                    <SectionHeader
                        level={2}
                        subtitle={jt`Change text and more on MakeLeaps stock Templates so they better fit your business. See the ${seeDetailsLinks.mlStockCustomTemplates} (Japanese site).`}
                        title={t`Stock Template Customization`}
                    />
                    <div className="mb-4 flex items-end justify-between">
                        <Filter
                            filterFormValue="filter_ml_based_template"
                            isAboveBreakpoint={isAboveBreakpoint}
                        />
                        <TooltipWrapper
                            message={
                                !canCreate.allowed && canCreate.failureReason
                            }
                        >
                            <DropdownAction
                                disabled={!canCreate.allowed}
                                label={t`Customize Template`}
                                layoutMethod="right-corner"
                                size="sm"
                            >
                                {documentTypes.map(
                                    (documentType) =>
                                        documentType.value && (
                                            <DropdownAction.Link
                                                href={window.Urls.customtemplate_create_view(
                                                    documentType.value,
                                                )}
                                                key={documentType.value}
                                            >
                                                {documentType.label}
                                            </DropdownAction.Link>
                                        ),
                                )}
                            </DropdownAction>
                        </TooltipWrapper>
                    </div>
                    <Table classNameTable="table-fixed" hasStripes={true}>
                        <Table.Header className="text-nowrap">
                            <Table.HeaderRow>
                                <Table.Column>{t`Display Name`}</Table.Column>
                                <Table.Column>{t`Document Type`}</Table.Column>
                                <Table.Column className="w-24">{t`Status`}</Table.Column>
                                <Table.Column
                                    className={twMerge(
                                        !isAboveBreakpoint &&
                                            getPreferredLanguage() === 'en' &&
                                            'w-60',
                                    )}
                                />
                            </Table.HeaderRow>
                        </Table.Header>
                        <Table.Body>
                            {mlStockCustomTemplates.length === 0 ? (
                                <Table.Row>
                                    <Table.Cell colSpan={4}>
                                        <div className="px-5 pb-7 pt-10 text-center">
                                            {t`No Customized Stock Templates found.`}
                                            <br />
                                            {t`Try changing your search terms or make a new Customization.`}
                                            <TooltipWrapper
                                                message={
                                                    !canCreate.allowed &&
                                                    canCreate.failureReason
                                                }
                                            >
                                                <DropdownAction
                                                    classNameAction="w-fit m-auto mt-4"
                                                    disabled={
                                                        !canCreate.allowed
                                                    }
                                                    label={t`Customize Template`}
                                                    layoutMethod="right-corner"
                                                    size="sm"
                                                >
                                                    {documentTypes.map(
                                                        (documentType) =>
                                                            documentType.value && (
                                                                <DropdownAction.Link
                                                                    href={window.Urls.customtemplate_create_view(
                                                                        documentType.value,
                                                                    )}
                                                                    key={
                                                                        documentType.value
                                                                    }
                                                                >
                                                                    {
                                                                        documentType.label
                                                                    }
                                                                </DropdownAction.Link>
                                                            ),
                                                    )}
                                                </DropdownAction>
                                            </TooltipWrapper>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ) : (
                                mlStockCustomTemplates.map((template) => (
                                    <Table.Row key={template.mid}>
                                        <Table.Cell>
                                            <Truncator
                                                href={template.urls.viewUrl}
                                                text={template.displayName}
                                                truncateAt="middle"
                                            />
                                        </Table.Cell>
                                        <Table.Cell className="whitespace-nowrap">
                                            <Truncator
                                                text={template.documentType}
                                                truncateAt="middle"
                                            />
                                        </Table.Cell>
                                        <Table.Cell className="whitespace-nowrap">
                                            {template.isArchived ? (
                                                <Pill
                                                    className="inline-block"
                                                    color={'grey_dark'}
                                                    label={t`Archived`}
                                                />
                                            ) : template.isDraft ? (
                                                <Pill
                                                    className="inline-block"
                                                    color="grey"
                                                    label={t`Draft`}
                                                ></Pill>
                                            ) : (
                                                <Pill
                                                    className="inline-block"
                                                    color="green"
                                                    label={t`Published`}
                                                ></Pill>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell className="flex items-center justify-end gap-2">
                                            {template.isDraft && (
                                                <TooltipWrapper
                                                    message={
                                                        !template.rights
                                                            .canPublish
                                                            .allowed &&
                                                        template.rights
                                                            .canPublish
                                                            .failureReason
                                                    }
                                                >
                                                    <LinkButton
                                                        disabled={
                                                            !template.rights
                                                                .canPublish
                                                                .allowed
                                                        }
                                                        href={
                                                            template.urls
                                                                .publishUrl
                                                        }
                                                        kind="secondary"
                                                        size="xs"
                                                    >{t`Publish`}</LinkButton>
                                                </TooltipWrapper>
                                            )}
                                            <TooltipWrapper
                                                message={
                                                    !template.isDraft
                                                        ? t`The Custom Template has been published`
                                                        : !template.rights
                                                                .canEdit.allowed
                                                          ? template.rights
                                                                .canEdit
                                                                .failureReason
                                                          : ''
                                                }
                                            >
                                                <LinkButton
                                                    disabled={
                                                        !template.rights.canEdit
                                                            .allowed
                                                    }
                                                    href={template.urls.editUrl}
                                                    kind="secondary"
                                                    size="xs"
                                                >{t`Edit`}</LinkButton>
                                            </TooltipWrapper>
                                            <TooltipWrapper
                                                message={
                                                    !canCreate.allowed &&
                                                    canCreate.failureReason
                                                }
                                            >
                                                <LinkButton
                                                    disabled={
                                                        !canCreate.allowed
                                                    }
                                                    href={
                                                        template.urls
                                                            .duplicateUrl
                                                    }
                                                    kind="secondary"
                                                    size="xs"
                                                >{t`Duplicate`}</LinkButton>
                                            </TooltipWrapper>
                                            <TooltipWrapper
                                                message={
                                                    !template.rights.canDelete
                                                        .allowed &&
                                                    template.rights.canDelete
                                                        .failureReason
                                                }
                                            >
                                                <Button
                                                    className="px-1.5"
                                                    disabled={
                                                        !template.rights
                                                            .canDelete.allowed
                                                    }
                                                    icon="trash"
                                                    kind="secondary"
                                                    onClick={() =>
                                                        handleDelete(
                                                            template.mid,
                                                        )
                                                    }
                                                    size="xs"
                                                />
                                            </TooltipWrapper>
                                        </Table.Cell>
                                    </Table.Row>
                                ))
                            )}
                        </Table.Body>
                    </Table>
                    <TablePagination
                        className="mb-5 mt-2"
                        hideForSinglePage={true}
                        isAmbigousStop={true}
                        onChange={(page) =>
                            updateUrlParamsAndReload('page', page)
                        }
                        onFirst={() => updateUrlParamsAndReload('page', 1)}
                        onLast={() => updateUrlParamsAndReload('page', 'last')}
                        pageCurrent={
                            paginationMlStockCustomTemplates.currentPage
                        }
                        pageStop={paginationMlStockCustomTemplates.numPages}
                        paginationPadding={
                            paginationMlStockCustomTemplates.windowPages
                        }
                    />
                </>
            )}
        </>
    );
};

export default Page;
